import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import './whatwedo.css';
import Corporate_new from './components/Corporate_new';
import Training from './components/Training';
import Education from './components/Education';
import Hospital from './components/Hospital';

function WhatWeDo() {
  const corporateRef = useRef(null);
  const educationalRef = useRef(null);
  const hospitalRef = useRef(null);
  const trainingRef = useRef(null);

  const SECTION_MAP = useMemo(() => ({
    scrollToCorporate: 'corporate-section',
    scrollToEducational: 'educational-section',
    scrollToHospital: 'hospital-section',
    scrollToTraining: 'training-section',
  }), []);

  const refMap = useMemo(() => ({
    'corporate-section': corporateRef,
    'educational-section': educationalRef,
    'hospital-section': hospitalRef,
    'training-section': trainingRef,
  }), []);

  const scrollToSection = useCallback((targetRef) => {
    if (targetRef?.current) {
      const rect = targetRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const elementHeight = rect.height;
      const viewportHeight = window.innerHeight;
      
      const absoluteElementTop = rect.top + scrollY;
      const centerPosition = absoluteElementTop - (viewportHeight / 2) + (elementHeight / 2);
      const scrollToPosition = Math.max(0, centerPosition);
      
      requestAnimationFrame(() => {
        window.scrollTo({ 
          top: scrollToPosition, 
          behavior: 'smooth' 
        });
      });
    }
  }, []);

  const scrollToTrainingSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const elementHeight = elementRect.height;
      const viewportHeight = window.innerHeight;
      
      const centerPosition = absoluteElementTop - (viewportHeight / 2) + (elementHeight / 2);
      const scrollToPosition = Math.max(0, centerPosition);
      
      window.scrollTo({
        top: scrollToPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const clearSessionStorage = useCallback(() => {
    Object.keys(SECTION_MAP).forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('SessionStorage not available:', error);
      }
    });
  }, [SECTION_MAP]);

  useEffect(() => {
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      setTimeout(() => {
        scrollToTrainingSection(scrollTarget);
        sessionStorage.removeItem('scrollTarget');
      }, 100);
      return;
    }

    let scrollKey = null;
    try {
      scrollKey = Object.keys(SECTION_MAP).find(key => sessionStorage.getItem(key));
    } catch (error) {
      console.warn('SessionStorage not available:', error);
      return;
    }

    if (scrollKey) {
      clearSessionStorage();
      
      const timeoutId = setTimeout(() => {
        const sectionId = SECTION_MAP[scrollKey];
        const targetRef = refMap[sectionId];
        
        if (targetRef) {
          scrollToSection(targetRef);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [SECTION_MAP, refMap, scrollToSection, scrollToTrainingSection, clearSessionStorage]);

  return (
    <div className="Our-services-root">
      <div className="Our-services-container">
        <h2 className="Our-services-title">Our Services</h2>
        <span className="Our-services-description">
          Our solutions are tailor made to specific requirements of Business & Corporate sectors.
        </span>
      </div>

      <div
        ref={corporateRef}
        id="corporate-section"
        className="Our-services-section"
      >
        <Corporate_new />
      </div>

      <div
        ref={educationalRef}
        id="educational-section"
        className="Our-services-section"
      >
        <Education />
      </div>

      <div
        ref={hospitalRef}
        id="hospital-section"
        className="Our-services-section"
      >
        <Hospital />
      </div>

      <div
        ref={trainingRef}
        id="training-section"
        className="Our-services-section"
      >
        <Training />
      </div>
    </div>
  );
}

export default React.memo(WhatWeDo);
